import { useState, useEffect, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './auth.css';
import { API_BASE } from '../../config';

export function VerifyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const stateEmail = (location.state as { email?: string })?.email || searchParams.get('email') || '';
  const urlCode = searchParams.get('code') || '';

  const [email, setEmail] = useState(stateEmail);
  const [code, setCode] = useState(urlCode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auto-submit if both email and code are in URL (from email link)
  useEffect(() => {
    if (stateEmail && urlCode && urlCode.length === 6) {
      handleAutoVerify(stateEmail, urlCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAutoVerify = async (autoEmail: string, autoCode: string) => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: autoEmail.trim(), code: autoCode.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message || 'Verification failed');
      setSuccess('Email verified! Redirecting to login...');
      setTimeout(() => navigate('/auth/login'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Enter the code manually.');
    }
  };
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!code.trim()) {
      setError('Verification code is required');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/v1/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error?.message || 'Verification failed');
      }

      setSuccess('Email verified! Redirecting to login...');
      setTimeout(() => navigate('/auth/login'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setError('Enter your email to resend the code');
      return;
    }

    setResending(true);
    setError('');

    try {
      // Re-register triggers Cognito to resend the code
      const res = await fetch(`${API_BASE}/v1/auth/verify/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (res.ok) {
        setSuccess('New code sent to your email.');
      }
    } catch {
      // Silently handle — don't reveal if email exists
      setSuccess('If the email is registered, a new code has been sent.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Verify your email</h1>
        <p className="auth-subtitle">
          Enter the 6-digit code we sent to {email || 'your email'}
        </p>

        {error && <div className="auth-error" role="alert">{error}</div>}
        {success && <div className="auth-success" role="status">{success}</div>}

        <form className="auth-form" onSubmit={handleVerify} noValidate>
          {!stateEmail && (
            <div className="form-field">
              <label htmlFor="verify-email">Email</label>
              <input
                id="verify-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
          )}

          <div className="form-field">
            <label htmlFor="verify-code">Verification code</label>
            <input
              id="verify-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              style={{ letterSpacing: '0.3em', fontSize: '1.25rem', textAlign: 'center' }}
            />
          </div>

          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <button
          type="button"
          className="auth-link"
          onClick={handleResend}
          disabled={resending}
          style={{ marginTop: 'var(--spacing-md)' }}
        >
          {resending ? 'Resending...' : "Didn't get the code? Resend"}
        </button>

        <div className="auth-footer">
          <Link to="/auth/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
